/**
 * A React hook for managing dismissable UI elements with a snooze period.
 *
 * This hook has a boolean and callback that can be used to set a snooze period for an arbitrary amount of time.
 * It's useful for banners, notifications, or other UI elements that should only be shown again after a certain time period has passed since dismissal.
 *
 * With this current iteration, we only need to check if there is a snooze on mount, or if a snooze is started.
 * That means an expired snooze will not trigger a rerender, and you'd need to reload to get the value to change.
 *
 * @param key - A unique string key used to store the snooze timestamp in localStorage.
 *              This key should be unique per dismissable element to avoid conflicts.
 * @param duration - The snooze period in milliseconds. After this time has passed since
 *                   the last dismissal, the element will be shown again. If no duration is passed,
 *                   the snooze will last forever (element will never be shown again).
 *
 * @returns A tuple containing:
 *   - `isSnoozing` (boolean): `true` if the element should be hidden (snooze active),
 *     `false` if the element should be shown (snooze expired or never set).
 *   - `startSnooze` (function): A function to call when the element is dismissed, which starts
 *     the snooze period and stores the current timestamp in localStorage.
 */
import { useCallback, useState } from 'react';

export const useSnooze = ({
  key,
  duration,
}: {
  key: string;
  duration?: number;
}): [boolean, () => void] => {
  const [snoozeState, setSnoozeState] = useState(() => {
    const dismissedAt = localStorage.getItem(key);

    if (!dismissedAt) return false;

    const dismissedTime = parseInt(dismissedAt, 10);
    const currentTime = Date.now();

    if (isNaN(dismissedTime)) return false;

    const snoozeForever = duration === undefined;
    if (snoozeForever) return true;

    const isSnoozing = currentTime - dismissedTime <= duration;

    return isSnoozing;
  });

  const startSnooze = useCallback(() => {
    const timestamp = Date.now();
    setSnoozeState(true);
    localStorage.setItem(key, timestamp.toString());
  }, [key]);

  return [snoozeState, startSnooze];
};
